import { FC, useCallback, useEffect, useState } from 'react';
import { Dropdown, Status, StatusType, Tooltip } from '@silverfort/sf-components';
import { useIspmContext } from '../../IspmContext';
import useMutateInsightEntityState from '../../api/mutations/useMutateInsightEntityState';
import { InsightEntity } from '../../api/types/Insights';
import { ENTITY_STATUS_OPTIONS, ENTITY_STATUS_TO_STATUS_TYPE_MAP, ENTITY_STATUS_TOOLTIP_MAP, getEntityStatusByInsightStatusDescriptions } from '../../common/entityStatus';
import { INSIGHT_STATUS_DESCRIPTIONS, INSIGHT_STATUSES } from '@silverfort-nodelibs/common/utils/consts';
import { ResolutionFeedbackPopup } from '../ResolutionFeedbackPopup/ResolutionFeedbackPopup';

type StatusColumnProps = {
    entity: InsightEntity;
    onSuccess?: (newStatusDescription: string) => void;
    onError?: (newStatusDescription: string) => void;
};

export const StatusColumn: FC<StatusColumnProps> = ({ entity, onSuccess, onError }) => {
    const [isResolutionPopupOpen, setIsResolutionPopupOpen] = useState(false);
    const { selectedInsight } = useIspmContext();
    const { mutate, isLoading } = useMutateInsightEntityState();
    const [selectedEntityStatus, setSelectedEntityStatus] = useState<string | undefined>(undefined);

    const entityStatusTooltip = entity.status_description ? ENTITY_STATUS_TOOLTIP_MAP[entity.status_description] : null;
    const statusType = selectedEntityStatus
        ? ENTITY_STATUS_TO_STATUS_TYPE_MAP[selectedEntityStatus]
        : StatusType.Default;

    useEffect(() => {
        if (!entity.status_description) {
            if (entity.status === INSIGHT_STATUSES.ACTIVE) {
                setSelectedEntityStatus(INSIGHT_STATUS_DESCRIPTIONS.OPEN);
                return;
            }
            return;
        }

        const entityStatus = getEntityStatusByInsightStatusDescriptions([entity.status_description])?.at(0);

        setSelectedEntityStatus(entityStatus);
    }, [entity.status_description, entity.status, isResolutionPopupOpen]);

    const handleStatusChange = useCallback(
        (selectedInsightStatus: string, selectedInsightStatusDescription: string) => {
            mutate({
                insightName: selectedInsight!,
                newStatus: selectedInsightStatus,
                newStatusDescription: selectedInsightStatusDescription,
                onError: () => {
                    onError?.(selectedInsightStatusDescription);

                    if (!entity.status_description) return;

                    const entityStatus = getEntityStatusByInsightStatusDescriptions([entity.status_description])?.at(0);

                    setSelectedEntityStatus(entityStatus);
                },
                onSuccess: () => {
                    onSuccess?.(selectedInsightStatusDescription);
                },
                selectedEntities: [entity]
            });
        },
        [entity, selectedInsight, mutate, onSuccess, onError]
    );

    const handleDropDownChange = useCallback(
        (newSelectedEntityStatuses?: string | string[]) => {
            if (!newSelectedEntityStatuses) return;

            const newSelectedEntityStatus = Array.isArray(newSelectedEntityStatuses)
                ? newSelectedEntityStatuses.at(0)
                : newSelectedEntityStatuses;

            if (!newSelectedEntityStatus) return;

            if (isSameStatusSelected(newSelectedEntityStatus, selectedEntityStatus)) return;
            
            setSelectedEntityStatus(newSelectedEntityStatus);

            if (newSelectedEntityStatus === INSIGHT_STATUS_DESCRIPTIONS.RESOLVED) {
                setIsResolutionPopupOpen(true);
            } else {
                handleStatusChange(INSIGHT_STATUSES.ACTIVE, newSelectedEntityStatus);
            }
        },
        [selectedEntityStatus, handleStatusChange]
    );

    const isSameStatusSelected = (
        newStatusDescription: string | undefined,
        currentStatusDescription: string | undefined
    ) => {
        return (
            newStatusDescription === currentStatusDescription &&
            newStatusDescription !== INSIGHT_STATUS_DESCRIPTIONS.RESOLVED
        );
    };

    const onResolutionPopupClose = useCallback(() => {
        setIsResolutionPopupOpen(false); 
    }, []);

    const dropdownContent = (
        <div className="inline-flex relative items-center">
            <Dropdown
                variant="trigger"
                selectedItems={selectedEntityStatus}
                items={ENTITY_STATUS_OPTIONS}
                onChange={handleDropDownChange}
                size="small"
                disabled={isLoading}
                className="pr-2 pl-5 border border-sf-gray-200 w-fit bg-sf-white"
            />

            <div className="absolute pl-2">
                <Status statusType={statusType} size="6px" />
            </div>
        </div>
    );

    return (
        <>
            {entityStatusTooltip ? <Tooltip title={entityStatusTooltip}>{dropdownContent}</Tooltip> : dropdownContent}

            <ResolutionFeedbackPopup
                handleStatusChange={handleStatusChange}
                selectedInsightStatusDescription={entity.status_description ?? ''}
                isOpen={isResolutionPopupOpen}
                onClose={onResolutionPopupClose}
            />
        </>
    );
};
